// Shared face-detection gate for profile-photo uploads (camera or gallery).
//
// Why this exists: a live camera capture during check-in/upload is always
// well-framed (the circular guide forces the face into the shot), so
// face-api.js reliably extracts a descriptor from it. A gallery-picked
// photo has no such guarantee — a group shot, an off-angle photo, poor
// lighting, or a face that's tiny relative to the crop — and can produce
// a weak or missing descriptor. If that photo gets saved as the official
// profile photo, every future check-in compares against it and silently
// fails to match more often, with no indication of *why* to the employee.
//
// The fix: run the exact same detection pipeline used at check-in time
// against the cropped photo BEFORE saving it, for every upload path
// (camera and gallery alike), and reject the save with a clear message
// if no face is detected — so a bad source photo never gets saved in the
// first place.
import * as faceapi from "@vladmandic/face-api";

let modelsLoadingPromise = null;

export async function loadFaceModels() {
  if (faceapi.nets.tinyFaceDetector.isLoaded) return;
  if (!modelsLoadingPromise) {
    modelsLoadingPromise = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri("/models"),
      faceapi.nets.faceRecognitionNet.loadFromUri("/models"),
    ]);
  }
  await modelsLoadingPromise;
}

// Returns { ok: true } or { ok: false, message } — never throws, so a
// model-load hiccup doesn't block someone from saving a photo entirely
// (falls back to "allow" with a console warning rather than a hard wall).
export async function validateFaceDetectable(canvas) {
  try {
    await loadFaceModels();
    const detection = await faceapi
      .detectSingleFace(canvas, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }))
      .withFaceLandmarks(true)
      .withFaceDescriptor();
    if (!detection) {
      return {
        ok: false,
        message: "No face detected in this photo. Reposition/zoom so your face fills the circle clearly, or pick a different photo — this photo will be used to verify your identity at check-in.",
      };
    }
    return { ok: true };
  } catch (e) {
    console.warn("[faceValidate] detection check failed, allowing upload:", e.message);
    return { ok: true };
  }
}
